import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  DocumentData,
} from "firebase/firestore";
import { createPortal } from "react-dom";

interface UnassignedUser {
  id: string;
  email?: string;
  displayName?: string;
  groupId?: string | null;
  [key: string]: any;
}

const NoGroupUsers: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<UnassignedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnassignedUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const usersRef = collection(db, "users");

      // Users with groupId == ""
      const emptyGroupQuery = query(usersRef, where("groupId", "==", ""));
      const emptySnap = await getDocs(emptyGroupQuery);

      // Users with groupId == null
      const nullGroupQuery = query(usersRef, where("groupId", "==", null));
      const nullSnap = await getDocs(nullGroupQuery);

      const merged: Record<string, UnassignedUser> = {};

      const addDocs = (snap: typeof emptySnap) => {
        snap.forEach((docSnap) => {
          // ❌ Skip template document
          if (docSnap.id === "template") return;

          const data = docSnap.data() as DocumentData;
          merged[docSnap.id] = {
            id: docSnap.id,
            email: data.email,
            displayName: data.displayName || data.name,
            groupId: data.groupId ?? null,
            ...data,
          };
        });
      };

      addDocs(emptySnap);
      addDocs(nullSnap);

      setUsers(Object.values(merged));
    } catch (err) {
      console.error("Error fetching unassigned users:", err);
      setError("Failed to load unassigned users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUnassignedUsers();
    }
  }, [isOpen]);

  return (
    <>
      {/* Menu item */}
      <li>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          Unassigned Users
        </a>
      </li>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="relative bg-white p-8 rounded-3xl shadow-2xl w-11/12 lg:w-3/4 max-h-[80vh] overflow-auto">
              <h2 className="text-2xl font-bold mb-4">Unassigned Users</h2>

              <button
                className="btn btn-sm btn-circle absolute right-4 top-4"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>

              <div className="mb-4 text-sm text-gray-600">
                <p>Users who do not belong to any group.</p>
                <p className="text-gray-500">("template" is excluded automatically)</p>
              </div>

              {loading && <p className="text-gray-500 text-sm">Loading users...</p>}

              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

              {!loading && !error && users.length === 0 && (
                <p className="text-gray-500 text-sm">All users are assigned to a group.</p>
              )}

              {!loading && !error && users.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-2xl shadow-inner">
                  <ul className="space-y-3">
                    {users.map((user) => (
                      <li
                        key={user.id}
                        className="bg-white px-4 py-3 rounded-xl shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">
                            {user.displayName || "Unnamed User"}
                          </p>
                          {user.email && (
                            <p className="text-sm text-gray-600">{user.email}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">ID: {user.id}</p>
                        </div>
                        <div className="text-xs text-gray-500 sm:text-right">
                          <span className="font-medium">groupId:</span>{" "}
                          {user.groupId === "" || user.groupId === null
                            ? "(none)"
                            : user.groupId}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default NoGroupUsers;